import React, { useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { motion } from 'framer-motion';
import { z } from 'zod';

import { Button } from 'components/ui/button';

import DialogModal from '../../../../../components/DialogModal/DialogModal';
import { cn } from '../../../../../components/lib/utils';
import {
  Collapsible,
  CollapsibleTrigger
} from '../../../../../components/ui/collapsible';
import { SidebarMenuItem } from '../../../../../components/ui/sidebar';
import { EBlockType, IBlock, IBlockContentProps } from '../../constants';
import { camelCaseToTitleCase, mapBlockTypeToIcon } from '../../utils';
import { pageFormSchema } from '../../validation-schema';

import { ContentsPreviewForm } from './Forms/ContentsPreviewForm';
import { DailyBonusForm } from './Forms/DailyBonusForm';
import { HtmlForm } from './Forms/HtmlForm';
import { ProgressBarForm } from './Forms/ProgressBarForm';
import { ItemsList } from './Items/ItemsList';

const BlockIcon: React.FC<{ type: EBlockType; isExpanded: boolean }> = ({
  type,
  isExpanded
}) => {
  return (
    <FontAwesomeIcon
      icon={mapBlockTypeToIcon[type]}
      className={cn(
        'text-sm text-indigo-300 w-9 h-9 flex items-center justify-center bg-[#F5F7FF] rounded-md',
        'group-hover/collapsible:text-indigo-500 group-hover/collapsible:bg-indigo-50',
        isExpanded && 'text-indigo-500 bg-indigo-50'
      )}
    />
  );
};

const mapBlockToBlockContent: Record<
  EBlockType,
  React.FC<IBlockContentProps>
> = {
  [EBlockType.IMAGE]: ItemsList,
  [EBlockType.VIDEO]: ItemsList,
  [EBlockType.CTA]: ItemsList,
  [EBlockType.HTML]: HtmlForm,
  [EBlockType.CONTENTS_PREVIEW]: ContentsPreviewForm,
  [EBlockType.DAILY_BONUS]: DailyBonusForm,
  [EBlockType.PROGRESS_BAR]: ProgressBarForm
};

interface BlockMenuItemProps {
  block: IBlock;
  index: number;
  isExpanded: boolean;
  isDragging?: boolean;
  isCurrentItemDragged?: boolean;
  onDelete: () => void;
  onToggleExpand: () => void;
  dragHandleListeners?: any;
  dragHandleAttributes?: any;
}

export const BlockMenuItem: React.FC<BlockMenuItemProps> = ({
  block,
  index,
  isExpanded,
  isDragging = false,
  isCurrentItemDragged = false,
  onDelete,
  onToggleExpand,
  dragHandleListeners,
  dragHandleAttributes
}) => {
  const { formState } = useFormContext<z.infer<typeof pageFormSchema>>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const collapseIcon = useMemo(
    () => (isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'),
    [isExpanded]
  );

  const blockTitle = useMemo(() => {
    if (block.type) {
      return camelCaseToTitleCase(block.type);
    }
    return `Block ${index + 1}`;
  }, [block.type, index]);

  const BlockContent = useMemo(
    () => mapBlockToBlockContent[block.type as EBlockType],
    [block.type]
  );

  const handleOpenChange = (open: boolean) => {
    if (open !== isExpanded) {
      onToggleExpand();
    }
  };

  const handleClickDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const hasBlocksErrors = useMemo(() => {
    const blockErrors = formState.errors.blocksV2;
    if (!blockErrors?.length) return false;
    if (blockErrors[index]) {
      return true;
    }
    return false;
  }, [formState.errors?.blocksV2?.length, index]);

  return (
    <div className="flex flex-col">
      <Collapsible
        className={cn('group/collapsible')}
        open={isExpanded}
        onOpenChange={handleOpenChange}
      >
        <SidebarMenuItem
          className={cn(
            'flex flex-col border rounded-md h-[61px] transition-colors',
            isExpanded
              ? 'border-indigo-500'
              : 'border-base-border hover:border-indigo-500',
            !isExpanded && hasBlocksErrors && 'border-[#F4BABA]'
          )}
        >
          <CollapsibleTrigger className="group flex justify-between items-center w-full h-full p-[10px] text-base-mutedForeground text-xs">
            <div
              className={cn(
                'leading-5 font-sans font-medium  flex items-center gap-[10px]',
                isExpanded && 'text-indigo-500'
              )}
            >
              <span
                {...(dragHandleListeners ? dragHandleListeners : {})}
                {...(dragHandleAttributes ? dragHandleAttributes : {})}
                className={cn(
                  'inline-flex items-center',
                  'cursor-grab active:cursor-grabbing'
                )}
              >
                <FontAwesomeIcon
                  icon={`fa-solid fa-grip-dots-vertical`}
                  className={cn(
                    'text-md transition-opacity w-[12px] text-base-foreground',
                    isDragging && !isCurrentItemDragged
                      ? 'opacity-0'
                      : 'opacity-0 group-hover/collapsible:opacity-100'
                  )}
                />
              </span>
              <BlockIcon type={block.type} isExpanded={isExpanded} />
              <div className="text-base-foreground font-medium text-sm">
                {blockTitle}
              </div>
              {hasBlocksErrors && (
                <FontAwesomeIcon
                  icon="fa-thin fa-circle-exclamation"
                  className="text-sm text-red-500"
                  title="This block has validation errors"
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                onClick={handleClickDelete}
                className={cn(
                  'p-1 rounded group-hover/collapsible:opacity-100 group-hover/collapsible:bg-gray-50 opacity-0 w-10 h-10 flex items-center justify-center'
                )}
                variant="ghost"
                title="Delete block"
              >
                <FontAwesomeIcon
                  icon="fa-regular fa-trash-can"
                  className="text-sm text-base-foreground"
                />
              </Button>
              <FontAwesomeIcon
                icon={`fa-solid ${collapseIcon}`}
                className={cn('text-xs text-base-mutedForeground')}
              />
            </div>
          </CollapsibleTrigger>
        </SidebarMenuItem>
      </Collapsible>
      <motion.div
        layout
        initial={{ height: 0 }}
        animate={{ height: isExpanded ? 'auto' : 0 }}
        transition={{ duration: 0.1, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <div className="flex flex-col gap-2 mt-2 ml-12">
          <BlockContent blockIndex={index} block={block} />
        </div>
      </motion.div>
      {isDeleteDialogOpen && (
        <DialogModal
          isOpen={isDeleteDialogOpen}
          dataTestId="PagesTable-DeleteModal"
          onOpenChange={(open: boolean) => {
            if (!open) setIsDeleteDialogOpen(false);
          }}
          title={'Delete Block'}
          description={
            "Are you sure you want to delete this block? This action can't be undone."
          }
          modalActions={{
            onConfirm: {
              label: 'Delete',
              action: () => {
                onDelete();
                setIsDeleteDialogOpen(false);
              }
            },
            onClose: {
              label: 'Cancel',
              action: () => setIsDeleteDialogOpen(false),
              variant: 'link'
            }
          }}
        />
      )}
    </div>
  );
};
